import React from "react";
import { motion } from "framer-motion";
import { FileSearch, Sparkles, UserCheck, Truck, LineChart } from "lucide-react";
import Chapter from "../site/Chapter";

const steps = [
  {
    icon: FileSearch,
    title: "Intake",
    tag: "Day 0",
    body: "A structured kickoff captures your goals, constraints, and priorities — every engagement starts from the same operational baseline.",
  },
  {
    icon: Sparkles,
    title: "AI-Assisted Setup",
    tag: "Day 1–2",
    body: "We stand up the AI-assisted workflow for your specific need — sourcing, support triage, or data processing — built around your existing tools.",
    ai: true,
  },
  {
    icon: UserCheck,
    title: "Human Review",
    tag: "Ongoing",
    body: "A trained specialist reviews every output against your brief. Noise out, real value in — consistent quality, every time.",
  },
  {
    icon: Truck,
    title: "Delivery",
    tag: "Ongoing",
    body: "Finished work lands where you need it — shortlists in your ATS, resolved tickets in your helpdesk, clean data in your reporting tool.",
  },
  {
    icon: LineChart,
    title: "Reporting",
    tag: "Weekly / Monthly",
    body: "Transparent reporting on what was done and what it produced — not just hours logged.",
  },
];

export default function HomeProcess() {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative py-24 sm:py-32 bg-[#070F1F] border-y border-slate-900"
    >
      <div className="absolute inset-0 bg-grid-faint opacity-50" />
      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10">
        <Chapter
          number="05"
          eyebrow="How It Works"
          accent="lime"
          title={
            <>
              One transparent process.{" "}
              <span className="font-serif italic">Adapts</span> to RPO, BPO, or KPO.
            </>
          }
          subtitle="AI-enabled workflows, specialist-owned. Consistent quality, every engagement."
        />

        {/* Desktop timeline */}
        <div className="mt-16 hidden lg:block">
          <div className="grid grid-cols-5 gap-4 relative">
            <svg
              className="absolute top-7 left-0 right-0 w-full h-2 pointer-events-none"
              viewBox="0 0 1200 8"
              preserveAspectRatio="none"
            >
              <line
                x1="40"
                y1="4"
                x2="1160"
                y2="4"
                stroke="#00E5FF"
                strokeWidth="1.5"
                strokeOpacity="0.4"
                className="flow-line"
              />
            </svg>

            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`process-step-${i}`}
                className="relative"
              >
                <div className="relative z-10 mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#0F1A30] border-2 border-[#00E5FF]/40 text-[#00E5FF] shadow-[0_0_30px_-8px_rgba(0,229,255,0.6)]">
                  <s.icon size={22} />
                </div>
                <div className="mt-5 text-center">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#A3FF5E]">
                    {s.tag}
                  </div>
                  <h4 className="font-display mt-1.5 text-base font-semibold text-white inline-flex items-center gap-2">
                    {s.title}
                    {s.ai && <span className="chip-ai text-[9px]">AI</span>}
                  </h4>
                  <p className="mt-2 text-xs leading-relaxed text-slate-400 px-2">
                    {s.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile timeline */}
        <div className="mt-12 lg:hidden space-y-5 relative">
          <div className="absolute left-7 top-7 bottom-7 w-px bg-gradient-to-b from-[#00E5FF]/40 via-[#A3FF5E]/30 to-transparent" />
          {steps.map((s, i) => (
            <div
              key={s.title}
              className="relative flex gap-5 items-start"
              data-testid={`process-step-mobile-${i}`}
            >
              <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#0F1A30] border-2 border-[#00E5FF]/40 text-[#00E5FF]">
                <s.icon size={22} />
              </div>
              <div className="pt-1 pb-2">
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#A3FF5E]">
                  {s.tag}
                </div>
                <h4 className="font-display mt-1 text-base font-semibold text-white inline-flex items-center gap-2">
                  {s.title}
                  {s.ai && <span className="chip-ai text-[9px]">AI</span>}
                </h4>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                  {s.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
